import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Navigation, ClipboardList, SendHorizontal } from "lucide-react";
import { apiFetch } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Eyebrow, PageTitle, PageSubtitle, Loader, EmptyState, Card, TripRoute } from "@/components/common";
import { DriverHome } from "./HomePage";
import { cn } from "@/lib/utils";

const TRIP_STATUS = { pending: "In attesa di assegnazione", available: "Disponibile", confirmed: "Assegnato", in_progress: "In corso" };
const MISSION_STATUS = { sent: "Inviato", seen: "Visualizzato", acknowledged: "Ricevuto e confermato" };
function when(v) {
  return v ? new Date(v).toLocaleString("it-IT", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" }) : "";
}

export default function MissionsPage() {
  const { user } = useAuth();
  const [missions, setMissions] = useState([]);
  const [transports, setTransports] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    (async () => {
      if (user?.account_type === "vettore") {
        const drivers = await apiFetch("/drivers").catch(() => []);
        const linked = drivers.filter((d) => d.driver_user_id);
        const groups = await Promise.all(linked.map((d) => apiFetch(`/drivers/${d.id}/missions`).catch(() => [])));
        if (alive) setMissions(groups.flat().sort((a, b) => String(b.sent_at).localeCompare(String(a.sent_at))));
      }
      if (user?.account_type === "azienda") {
        const mine = await apiFetch("/transports").catch(() => []);
        if (alive) setTransports(mine.filter((t) => !["completed", "cancelled", "transferred"].includes(t.status)));
      }
      if (alive) setLoading(false);
    })();
    return () => {
      alive = false;
    };
  }, [user]);

  if (user?.account_type === "autista") return <DriverHome />;

  return (
    <div className="space-y-3">
      <Eyebrow>OPERATIVITÀ</Eyebrow>
      <PageTitle>Missioni</PageTitle>
      <PageSubtitle>
        {user?.account_type === "azienda"
          ? "I trasporti attivi della tua azienda e il loro stato di avanzamento."
          : "Missioni inviate, stato di visualizzazione e conferme degli autisti."}
      </PageSubtitle>

      {loading ? (
        <Loader />
      ) : user?.account_type === "azienda" ? (
        transports.length === 0 ? (
          <EmptyState icon={Navigation} title="Nessuna missione attiva" text="Quando un tuo viaggio viene preso in carico da un vettore, ne segui l'avanzamento da qui." />
        ) : (
          transports.map((t) => (
            <Link key={t.id} to={`/trip/${t.id}`} data-testid={`company-mission-${t.id}`}>
              <Card className="space-y-2.5 hover:shadow-md">
                <TripRoute trip={t} />
                <p className="text-xs text-muted-foreground">{t.date}{t.time ? ` · ${t.time}` : ""} · {t.vehicle_type}</p>
                <div className="flex items-center justify-between border-t border-border pt-2.5">
                  <span className={cn("text-xs font-extrabold", t.status === "in_progress" ? "text-[hsl(var(--warning))]" : t.status === "confirmed" ? "text-[hsl(var(--success))]" : "text-muted-foreground")}>
                    {TRIP_STATUS[t.status] || t.status}
                  </span>
                  <span className="text-[11px] text-muted-foreground">{t.assigned_to_name || "In attesa di vettore"}</span>
                </div>
              </Card>
            </Link>
          ))
        )
      ) : user?.account_type !== "vettore" ? (
        <EmptyState icon={ClipboardList} title="Nessuna missione operativa" text="Le missioni sono gestite dal vettore verso i propri autisti." />
      ) : missions.length === 0 ? (
        <EmptyState icon={SendHorizontal} title="Nessuna missione inviata" text="Vai in Autisti per inviare la prima missione." />
      ) : (
        missions.map((m) => (
          <Card key={m.id} className="space-y-2.5">
            <div className="flex items-center gap-3">
              <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-secondary">
                <Navigation className="h-5 w-5 text-primary" />
              </span>
              <div className="flex-1">
                <p className="font-extrabold text-foreground">{m.pickup_address} → {m.delivery_address}</p>
                <p className="text-xs text-muted-foreground">{m.driver_name || "Autista"} · {m.date}{m.time ? ` · ${m.time}` : ""}</p>
              </div>
            </div>
            <div className="flex items-center justify-between border-t border-border pt-2.5">
              <span className={cn("text-xs font-extrabold", m.status === "acknowledged" ? "text-[hsl(var(--success))]" : m.status === "seen" ? "text-[hsl(var(--warning))]" : "text-muted-foreground")}>
                {MISSION_STATUS[m.status]}
              </span>
              <span className="text-[11px] text-muted-foreground">
                {m.status === "acknowledged" ? when(m.acknowledged_at) : m.status === "seen" ? when(m.seen_at) : when(m.sent_at)}
              </span>
            </div>
          </Card>
        ))
      )}
    </div>
  );
}
